import React, { useState, useEffect, useCallback } from 'react';
import { CreditCard, TrendingUp, Users, DollarSign, RefreshCw } from 'lucide-react';
import { PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { supabase } from '../../../lib/supabase';

const COLORS = ['#00E5FF', '#7B2FBE', '#10B981', '#F59E0B'];

const cancellationReasons = [
  { name: 'Too Expensive', value: 38 },
  { name: 'Missing Features', value: 27 },
  { name: 'Not Using', value: 22 },
  { name: 'Other', value: 13 },
];

export default function SubscriptionManagement() {
  const [stats, setStats] = useState({ free: 0, premium: 0, mrr: 0, churnRate: 2.4 });
  const [subscriptions, setSubscriptions] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [overrideUser, setOverrideUser] = useState(null);
  const [overridePlan, setOverridePlan] = useState('free');
  const [overrideMsg, setOverrideMsg] = useState('');
  const [searchEmail, setSearchEmail] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: budgets, error } = await supabase?.from('budgets')?.select('id, user_id, plan, created_at, updated_at, user_profiles(email, full_name)');
      if (!error && budgets) {
        const premium = budgets?.filter(b => b?.plan === 'premium')?.length || 0;
        const free = budgets?.filter(b => b?.plan !== 'premium')?.length || 0;
        setStats({ free, premium, mrr: premium * 9.99, churnRate: 2.4 });
        setSubscriptions(budgets?.slice(0, 20) || []);

        // Build real revenue trend from premium budget creation dates
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const now = new Date();
        const revenueByMonth = Array.from({ length: 12 }, (_, i) => {
          const monthIndex = (now?.getMonth() - 11 + i + 12) % 12;
          const year = now?.getFullYear() - (now?.getMonth() - 11 + i < 0 ? 1 : 0);
          const monthStr = String(monthIndex + 1)?.padStart(2, '0');
          const yearMonthPrefix = `${year}-${monthStr}`;
          const premiumInMonth = budgets?.filter(b =>
            b?.plan === 'premium' && b?.created_at?.startsWith(yearMonthPrefix)
          )?.length || 0;
          return { month: months?.[monthIndex], revenue: premiumInMonth * 9.99 };
        });
        setRevenueData(revenueByMonth);
      }
    } catch (err) {
      console.error('Load subscriptions error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Real-time subscription for budget/subscription changes
  useEffect(() => {
    const channel = supabase?.channel('admin_subscriptions_realtime')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'budgets' }, () => {
        loadData();
      })
      ?.subscribe();
    return () => supabase?.removeChannel(channel);
  }, [loadData]);

  const handleOverride = async () => {
    if (!overrideUser) return;
    try {
      await supabase?.from('budgets')?.update({ plan: overridePlan })?.eq('user_id', overrideUser?.user_id);
      setOverrideMsg(`Plan updated to ${overridePlan} for ${overrideUser?.user_profiles?.email || overrideUser?.user_id}.`);
      setOverrideUser(null);
      loadData();
      setTimeout(() => setOverrideMsg(''), 4000);
    } catch (err) {
      console.error('Override error:', err);
    }
  };

  const filtered = subscriptions?.filter(s =>
    !searchEmail || s?.user_profiles?.email?.toLowerCase()?.includes(searchEmail?.toLowerCase())
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Subscription Management</h2>
        <button onClick={loadData} className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs" style={{ background: 'rgba(0,229,255,0.08)', color: '#00E5FF', border: '1px solid rgba(0,229,255,0.2)', fontFamily: 'DM Sans, sans-serif' }}>
          <RefreshCw size={12} /> Refresh
        </button>
      </div>
      {overrideMsg && (
        <div className="px-4 py-2.5 rounded-xl text-sm" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', color: '#10B981', fontFamily: 'DM Sans, sans-serif' }}>{overrideMsg}</div>
      )}
      {/* Overview Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[{ icon: Users, label: 'Free Users', value: stats?.free?.toLocaleString(), color: '#00E5FF' },
          { icon: CreditCard, label: 'Premium Users', value: stats?.premium?.toLocaleString(), color: '#7B2FBE' },
          { icon: DollarSign, label: 'Est. MRR', value: `$${stats?.mrr?.toFixed(0)}`, color: '#10B981' },
          { icon: TrendingUp, label: 'Churn Rate', value: `${stats?.churnRate}%`, color: '#F59E0B' }]?.map(item => (
          <div key={item?.label} className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,229,255,0.1)' }}>
            <item.icon size={18} style={{ color: item?.color }} className="mb-2" />
            <p className="text-xl font-bold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>{item?.value}</p>
            <p className="text-xs" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{item?.label}</p>
          </div>
        ))}
      </div>
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,229,255,0.1)' }}>
          <h3 className="text-sm font-semibold text-white mb-4" style={{ fontFamily: 'DM Sans, sans-serif' }}>Cancellation Reasons</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={cancellationReasons} cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={4} dataKey="value">
                {cancellationReasons?.map((_, i) => <Cell key={i} fill={COLORS?.[i % COLORS?.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: '#0D1B2A', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 8, color: '#E2E8F0', fontSize: 12 }} />
              <Legend formatter={v => <span style={{ color: '#94A3B8', fontSize: 11, fontFamily: 'DM Sans, sans-serif' }}>{v}</span>} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,229,255,0.1)' }}>
          <h3 className="text-sm font-semibold text-white mb-4" style={{ fontFamily: 'DM Sans, sans-serif' }}>Revenue Trend (12 months)</h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="month" tick={{ fill: '#64748B', fontSize: 10 }} tickLine={false} />
              <YAxis tick={{ fill: '#64748B', fontSize: 10 }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: '#0D1B2A', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 8, color: '#E2E8F0', fontSize: 12 }} formatter={v => [`$${v}`, 'Revenue']} />
              <Line type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Subscription Table with Manual Override */}
      <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,229,255,0.1)' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Subscription Records</h3>
          <input type="text" value={searchEmail} onChange={e => setSearchEmail(e?.target?.value)} placeholder="Filter by email..."
            className="px-3 py-1.5 rounded-lg text-xs text-white placeholder-slate-600 outline-none"
            style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.15)', fontFamily: 'DM Sans, sans-serif', width: 180 }} />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(0,229,255,0.08)' }}>
                {['User', 'Plan', 'Since', 'Actions']?.map(h => (
                  <th key={h} className="pb-3 text-left text-xs font-semibold" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="py-8 text-center text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Loading...</td></tr>
              ) : filtered?.length === 0 ? (
                <tr><td colSpan={4} className="py-8 text-center text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>No records found</td></tr>
              ) : filtered?.map((sub, i) => (
                <tr key={sub?.id} style={{ borderTop: i > 0 ? '1px solid rgba(0,229,255,0.05)' : 'none' }}>
                  <td className="py-2.5">
                    <p className="text-sm text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>{sub?.user_profiles?.full_name || 'Unknown'}</p>
                    <p className="text-xs" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{sub?.user_profiles?.email || sub?.user_id}</p>
                  </td>
                  <td className="py-2.5">
                    <span className="text-xs px-2 py-1 rounded-full capitalize" style={{ background: sub?.plan === 'premium' ? 'rgba(123,47,190,0.15)' : 'rgba(0,229,255,0.08)', color: sub?.plan === 'premium' ? '#7B2FBE' : '#00E5FF', fontFamily: 'DM Sans, sans-serif' }}>{sub?.plan || 'free'}</span>
                  </td>
                  <td className="py-2.5 text-xs" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{sub?.created_at ? new Date(sub?.created_at)?.toLocaleDateString() : 'N/A'}</td>
                  <td className="py-2.5">
                    {overrideUser?.id === sub?.id ? (
                      <div className="flex items-center gap-2">
                        <select value={overridePlan} onChange={e => setOverridePlan(e?.target?.value)} className="px-2 py-1 rounded-lg text-xs outline-none" style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(0,229,255,0.2)', color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>
                          <option value="free">Free</option><option value="premium">Premium</option>
                        </select>
                        <button onClick={handleOverride} className="px-2 py-1 rounded-lg text-xs text-white" style={{ background: 'linear-gradient(135deg, #00E5FF, #7B2FBE)', fontFamily: 'DM Sans, sans-serif' }}>Save</button>
                        <button onClick={() => setOverrideUser(null)} className="px-2 py-1 rounded-lg text-xs" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Cancel</button>
                      </div>
                    ) : (
                      <button onClick={() => { setOverrideUser(sub); setOverridePlan(sub?.plan || 'free'); }} className="px-2.5 py-1.5 rounded-lg text-xs" style={{ background: 'rgba(0,229,255,0.08)', color: '#00E5FF', border: '1px solid rgba(0,229,255,0.15)', fontFamily: 'DM Sans, sans-serif' }}>Override Plan</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
